import React, { useState, useRef, useEffect } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { ListBulletIcon } from '@heroicons/react/24/solid';
import { useOfflineStatus } from 'services/OfflineStatusContext';
import SyncQueueModal from './SyncQueueModal';

const SPHERE = 'inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white';

const StatusMark = ({ variant }) => {
  if (variant === 'error') {
    return (
      <span className={`${SPHERE} bg-red-500`}>
        <span className="text-[13px] font-bold leading-none text-white">!</span>
      </span>
    );
  }
  if (variant === 'loading') {
    return (
      <span className={`${SPHERE} bg-white`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-yellow-500" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className={`${SPHERE} bg-green-500`}>
      <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
    </span>
  );
};

const OfflineStatusBadge = () => {
  const { isOnline, isSyncing, isFetchingData, pendingCount, retryingCount, failedCount, conflictCount, currentItem, totalItems } = useOfflineStatus();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ##### CLOSE DROPDOWN IF CLICKED OUTSIDE
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ##### COMPUTED SYNC STATE
  // Show syncing if either writes (isSyncing) or reads (isFetchingData) are active
  const isSyncingAnything = isSyncing || isFetchingData;

  // ##### GET STATUS VARIANT AND COLOR
  const getStatus = () => {
    if (!isOnline) return { variant: 'error', color: 'text-red-500' };
    if (isSyncingAnything) return { variant: 'loading', color: 'text-yellow-500' };
    return { variant: 'ready', color: 'text-green-500' };
  };

  // ##### GET STATUS TEXT
  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (isSyncingAnything) return 'Syncing';
    return 'Online';
  };

  const { variant, color } = getStatus();
  const statusText = getStatusText();

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/10 transition-colors"
      >
        <StatusMark variant={variant} />
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
          <div className="px-4 py-2">
            <p className="text-black font-bold text-sm mb-1">Sync Status</p>
            <p className={`text-xs ${color} font-medium`}>{statusText}</p>
          </div>
          
          <hr className="border-t border-gray-200 my-2" />
          
          {isSyncing && totalItems > 0 && (
            <div className="px-4 py-2">
              <p className="text-xs text-gray-600">
                Syncing writes: {currentItem} of {totalItems} items...
              </p>
            </div>
          )}
          
          {isFetchingData && !isSyncing && (
            <div className="px-4 py-2">
              <p className="text-xs text-gray-600">
                Syncing data...
              </p>
            </div>
          )}
          
          {pendingCount > 0 && (
            <div className="px-4 py-2">
              <p className="text-xs text-gray-700">
                <span className="font-semibold">{pendingCount}</span> pending
              </p>
            </div>
          )}
          
          {failedCount > 0 && (
            <div className="px-4 py-2">
              <p className="text-xs text-red-600">
                <span className="font-semibold">{failedCount}</span> failed
              </p>
            </div>
          )}
          
          {retryingCount > 0 && (
            <div className="px-4 py-2">
              <p className="text-xs text-blue-600">
                <span className="font-semibold">{retryingCount}</span> retrying
              </p>
            </div>
          )}
          
          {conflictCount > 0 && (
            <div className="px-4 py-2">
              <p className="text-xs text-yellow-600">
                <span className="font-semibold">{conflictCount}</span> conflict{conflictCount > 1 ? 's' : ''}
              </p>
            </div>
          )}
          
          {pendingCount === 0 && retryingCount === 0 && failedCount === 0 && conflictCount === 0 && isOnline && !isSyncingAnything && (
            <div className="px-4 py-2">
              <p className="text-xs text-gray-500">All synced</p>
            </div>
          )}

          <button
            onClick={() => {
              setDropdownOpen(false);
              setModalOpen(true);
            }}
            className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 text-xs"
          >
            <ListBulletIcon className="h-4 w-4 mr-2" />
            View Queue {(pendingCount + retryingCount + failedCount + (conflictCount || 0)) > 0 && `(${pendingCount + retryingCount + failedCount + (conflictCount || 0)})`}
          </button>
        </div>
      )}

      <SyncQueueModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default OfflineStatusBadge;
